import axios from 'axios';
import config from '../../config';
import { getRetryDelay, handleError, isRetryableError } from '../errorHandler';

// Create axios instance
const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000, // 30 second timeout
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// Request interceptor - add retry metadata
api.interceptors.request.use(
  (config) => {
    // Initialize retry count
    config.metadata = { retryCount: 0 };
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with error handling and retry logic
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Skip retry for cancelled requests
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Check if request can be retried
    if (isRetryableError(error) && originalRequest && !originalRequest._retry) {
      const retryCount = originalRequest.metadata?.retryCount || 0;
      const maxRetries = originalRequest.maxRetries || 3;

      if (retryCount < maxRetries) {
        originalRequest._retry = true;
        originalRequest.metadata.retryCount = retryCount + 1;

        // Calculate delay with exponential backoff
        const delay = getRetryDelay(retryCount + 1);

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Retry the request
        return api(originalRequest);
      }
    }


    // Attach processed error info to error object
    error.errorInfo = handleError(error, {
      context: {
        url: originalRequest?.url,
        method: originalRequest?.method,
        retryCount: originalRequest?.metadata?.retryCount || 0,
      },
    });

    // For 401 errors, dispatch logout event
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    return Promise.reject(error);
  }
);

/**
 * Helper to make API requests with custom retry configuration
 */
export const apiWithRetry = (config, maxRetries = 3) => {
  return api({
    ...config,
    maxRetries,
  });
};

/**
 * Helper to make API requests without retry
 */
export const apiWithoutRetry = (config) => {
  return api({
    ...config,
    maxRetries: 0,
  });
};

export default api;
